// 웹퍼널 → 앱 핸드오프 시 웹퍼널에 마지막으로 로그인한 계정과 앱에 로그인된
// 계정의 불일치를 감지하기 위한 유틸.
// 계약: deep_link_value=last_login_method 링크에서 sub1=provider,
//       sub2=계정 해시(아래 형식). 다른 deep_link_value 의 sub2 와는 무관하다.
// - hashAccountId: userId 를 OneLink deep_link_sub2 에 실을 단방향 해시로 변환
//   (원본 userId 를 마케팅 URL/AppsFlyer 에 노출하지 않기 위함. 상호 대조 전용)
// - shouldShowAccountMismatch: 미스매치 안내 모달 표시 여부 판단

const HASH_SALT = "distancing-account-hint-v1";
const HASH_LENGTH = 16;

// hashAccountId 산출물의 정확한 형태(16자리 소문자 hex).
// 수신 측 검증에 사용 — 형식이 다른 값은 신뢰하지 않는다.
export const ACCOUNT_HASH_REGEX = /^[0-9a-f]{16}$/;

// 힌트 유효 기간: CTA 클릭 후 이 시간이 지나면 비교하지 않는다 (오래된 힌트 오탐 방지)
export const ACCOUNT_HINT_TTL_MS = 72 * 60 * 60 * 1000;

export async function hashAccountId(
  userId: number
): Promise<string | undefined> {
  try {
    const data = new TextEncoder().encode(`${HASH_SALT}:${userId}`);
    const digest = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, HASH_LENGTH);
  } catch {
    // crypto.subtle 미지원(비보안 컨텍스트 등) 시 힌트 없이 동작
    return undefined;
  }
}

export interface LastLoginAccountHint {
  hash: string;
  // 네이티브가 딥링크 "도착" 시점에 발급하는 클릭 식별자.
  // 재송신(웹뷰 ready 마다)은 같은 clickId 를 유지하므로,
  // clickId 가 바뀔 때만 새 CTA 클릭으로 취급한다(닫기 해제·TTL 갱신).
  clickId: string;
  receivedAt: number;
  // 웹퍼널 계정의 소셜 provider (수신 시점의 lastLoginMethod 이벤트에서 캡처).
  // 모달 안내 문구용 — loginHintAtom 은 현재 로그인 계정으로 덮이므로 쓰지 않는다.
  provider?: "kakao" | "google" | "apple";
  // 사용자가 "닫기"로 무시한 힌트 (같은 clickId 동안 다시 띄우지 않음)
  dismissedHash?: string;
}

export function shouldShowAccountMismatch({
  hint,
  myAccountHash,
  isLoggedIn,
  isInWebview,
  now,
}: {
  hint?: LastLoginAccountHint;
  myAccountHash?: string;
  isLoggedIn: boolean;
  isInWebview: boolean;
  now: number;
}): boolean {
  if (!isInWebview || !isLoggedIn) return false;
  if (!hint?.hash || !myAccountHash) return false;
  if (hint.hash === myAccountHash) return false;
  if (hint.dismissedHash === hint.hash) return false;
  if (now - hint.receivedAt > ACCOUNT_HINT_TTL_MS) return false;
  return true;
}
